import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import {
  type TableColumn,
  createEnumColumn,
  createNumberColumn,
  createRoutingFilter,
  createTextColumn,
} from '@ipfabric/utils';
import { formatDeleteRowsConfirmDialogMessages } from 'components/ApiTable/helpers/formatDeleteRowsConfirmDialogMessages';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import type { SimpleConfirmDialogHook } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { type FC, memo, useMemo } from 'react';
import { AddSettingDialog } from './AddSettingDialog';
import { EditSettingDialog } from './EditSettingDialog';
import type { CustomPortSettingIndexed } from './model';
import type {
  CustomPortSettingsCommands,
  CustomPortSettingsShowables,
} from './useCustomPortSettings';

interface Props {
  commands: CustomPortSettingsCommands;
  confirmDialog: SimpleConfirmDialogHook;
  customPortSettingsIndexed: CustomPortSettingIndexed[];
  showables: CustomPortSettingsShowables;
  menuItems?: TableActionsItem[];
  borderless?: boolean;
  tableClassName?: string;
}

const BareCustomPortSettings: FC<Props> = ({
  commands,
  confirmDialog,
  customPortSettingsIndexed,
  showables,
  menuItems,
  borderless = true,
  tableClassName,
}) => {
  const msg = useLocale().discoverySettings.customPortSettings;
  const deletionMsg = useLocale().generic.tableDeletionHelper;

  const columns: TableColumn<CustomPortSettingIndexed>[] = useMemo(
    () => [
      createTextColumn({
        key: 'label',
        title: msg.columns.label,
      }),
      createEnumColumn({
        key: 'protocol',
        title: msg.columns.protocol,
        options: msg.protocols,
      }),
      createNumberColumn({
        key: 'port',
        title: msg.columns.port,
      }),
      createTextColumn({
        key: 'subnets',
        title: msg.columns.subnets,
        array: true,
        filter: createRoutingFilter(),
      }),
      createTextColumn({
        key: 'excludeSubnets',
        title: msg.columns.excludeSubnets,
        array: true,
        filter: createRoutingFilter(),
      }),
      createFrontendOnlyColumn({
        key: 'action',
        disabledSort: true,
        title: msg.columns.action,
        render: (setting: CustomPortSettingIndexed) => (
          <div className='flex flex-row items-center gap-xs'>
            <TableButton
              variant='plain'
              icon='fa-pen'
              size='small'
              onClick={() => {
                showables.editSetting.show({ setting });
              }}
              type='button'
            />
            <TableButton
              variant='plain'
              accent='negative'
              icon='fa-trash-can'
              size='small'
              onClick={async () => {
                if (
                  await confirmDialog.show(
                    formatDeleteRowsConfirmDialogMessages(
                      deletionMsg.deleteConfirmDialog,
                      1,
                    ),
                  )
                ) {
                  commands.deleteSettings({
                    indexes: new Set([setting.index]),
                  });
                }
              }}
              type='button'
              dataTestId={`ports-${setting.label}-remove-icon`}
            />
          </div>
        ),
      }),
    ],
    [
      msg,
      commands.deleteSettings,
      deletionMsg,
      confirmDialog.show,
      showables.editSetting.show,
    ],
  );

  const bulkActions: TableBulkAction[] = useMemo(
    () => [
      {
        key: 'delete',
        title: 'Delete',
        accent: 'negative',
        icon: 'fa-trash-alt',
        disableAutoDeselect: true,
        onClick: async ({
          selectedRows,
          deselectAll,
        }: TableBulkActionClickEvent) => {
          if (
            await confirmDialog.show(
              formatDeleteRowsConfirmDialogMessages(
                deletionMsg.deleteConfirmDialog,
                selectedRows.size,
              ),
            )
          ) {
            deselectAll();
            commands.deleteSettings({ indexes: selectedRows as Set<number> });
          }
        },
      },
    ],
    [deletionMsg, commands.deleteSettings, confirmDialog.show],
  );

  return (
    <>
      <AddSettingDialog commands={commands} showable={showables.addSetting} />
      <EditSettingDialog commands={commands} showable={showables.editSetting} />
      <Table
        borderless={borderless}
        className={tableClassName}
        rowHeight={44}
        columns={columns}
        bulkActions={bulkActions}
        data={customPortSettingsIndexed}
        loadRowIdsForSelectAll='fromData'
        rowIdField='index'
        title={msg.title}
        menuItems={menuItems}
      />
    </>
  );
};

export const CustomPortSettings = memo(BareCustomPortSettings);
